import { useEffect, useRef, useState, type FC } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { notificationEndpoints } from "../../../../services/api/notification"
import NotificationBellButton from "../../molecules/NotificationBellButton"
import NotificationModal from "../../molecules/notificationModal/NotificationModal"
import { navigateToUrl } from "single-spa"
import { useConfigProvider } from "../../../../config/ConfigProvider"
import { NotificationsModel, MarkAsReadAction } from "../../../../types/NotificationsModel"
import { toast } from "react-toastify"
import { ErrorIcon } from "../../../../assets/icons/icons"

type Props = {
    notificationIconBackGroundColor?: string
}

const NotificationBell: FC<Props> = (props) => {
    const {notificationIconBackGroundColor} = props
    const { getNotifications, deleteNotificationsMarkAsRead, deleteNotificationsMarkAllAsRead } = notificationEndpoints()
    const [showNotificactionModal, setShowNotificactionModal] = useState<boolean>(false)
    const {notificationBellUpdateFrequency} = useConfigProvider()
    const [message, setMessage] = useState<string>('')
    const [unreadNotificationsCount,setUnreadNotificationsCount] = useState(0)
    const queryClient = useQueryClient()
    const queryKey = ['getNotifications']
    const channelRef = useRef<BroadcastChannel | null>(null)
    const [notificationData, setNotificationData] = useState<NotificationsModel>()

    const {
        data: notifications,
        isLoading,
        isRefetching,
        isError,
        isLoadingError,
        isRefetchError,
         refetch,
    } = useQuery<NotificationsModel>({
        queryKey: queryKey,
        queryFn: async ({signal}) => await getNotifications(signal),
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    })
    
    const hasError = isError || isLoadingError || isRefetchError
    const loadingData = (isLoading || isRefetching || !notifications) && !hasError
    const notificationsRef = useRef(notificationData)
    
    const deleteNotificationsMarkAsReadMutation = useMutation<Error, string, string>({
        mutationFn: (value) =>
            deleteNotificationsMarkAsRead(value),
        onError: (err) => console.error(err),
      })

    const deleteNotificationsMarkAllAsReadMutation = useMutation<Error, string>({
        mutationFn: () =>
            deleteNotificationsMarkAllAsRead(),
        onError: (err) => console.error(err),
      })

    const handleRefreshNotifications = async () => {
        await queryClient.cancelQueries({queryKey});
        refetch();
    }

    const handleGoToNotifications = () => {
        navigateToUrl("/notifications");
    }

    const handleMarkAsRead: MarkAsReadAction  = async (value: string, status: boolean) => {
        setShowNotificactionModal(false)
        if (status == false){
        const response = await deleteNotificationsMarkAsReadMutation.mutateAsync(value)
        if (response) {
            queryClient.refetchQueries({
              queryKey: queryKey,
            })
          } else {
            toast.error("An error occurred while trying to mark notifications as read", {
              autoClose: 3000,
              icon: ErrorIcon,
              toastId: "api-error-toast",
            })
          }
        }
    }

    const handleMarkAllAsRead = async () => {
        const response = await deleteNotificationsMarkAllAsReadMutation.mutateAsync()
        if (response) {
            channelRef.current?.postMessage({ type: 'MARK_ALL_AS_READ_FROM_BELL', unreadNotificationCount: 0 })
            setAllNotificatioToRead(notificationData)
            setUnreadNotificationsCount(0)
          } else {
            toast.error("An error occurred while trying to mark notifications as read", {
              autoClose: 3000,
              icon: ErrorIcon,
              toastId: "api-error-toast",
            })
          }
    }

    useEffect(() => {
        if (notifications) {
            setNotificationData(notifications)
        }
    }, [notifications])

    useEffect(() => {
        notificationsRef.current = notificationData
    }, [notificationData])

    useEffect(() => {
        if(hasError){
            setMessage('We’re sorry, we’re having trouble retrieving this right now. Please try refreshing your browser or ')
            setUnreadNotificationsCount(0)
        }
        else if (notificationData?.totalNotifications == 0){
            setMessage('You do not currently have any notifications.')
            setUnreadNotificationsCount(0)
        }
        else if (notificationData?.totalLatestNotifications == 0){ 
            setMessage('You do not currently have any notifications within the last 30 days.')
            setUnreadNotificationsCount(0)
        }
        else{
            setMessage('')
            if (!loadingData) {
                setUnreadNotificationsCount(notificationData?.unreadNotificationCount ?? 0)
                channelRef.current?.postMessage({ type: 'UPDATE_UNREAD_COUNT_FROM_BELL', unreadNotificationCount: notificationData?.unreadNotificationCount ?? 0 })
            }
        }
    }, [notificationData, loadingData, hasError])

    useEffect(() => {
        const refreshIntervalId = setInterval(() => {
            handleRefreshNotifications()
            channelRef.current?.postMessage({ type: 'INTERVAL_NEXT_EXECUTION' })
        }, +notificationBellUpdateFrequency)

        return () => clearInterval(refreshIntervalId)
    }, [])

    useEffect(() => {
        const channel = new BroadcastChannel('notifications')
        channelRef.current = channel

        channel.onmessage = (event) => {
            const currentNotifications = notificationsRef.current
            if (event.data?.type == 'MARK_ALL_AS_READ_FROM_NOTIF_APP') {
                setUnreadNotificationsCount(0)
                setAllNotificatioToRead(currentNotifications)
            }
            if (event.data?.type == 'MARK_AS_READ_FROM_NOTIF_APP') {
                refetch()
            }
        }
        return () => {
            channel.close()
        }
    }, [])

    function setAllNotificatioToRead(notificationsToRead: NotificationsModel | undefined) {
        notificationsToRead?.notifications?.forEach(notifications => {
            notifications.notificationList.forEach(prop => {
                prop.read = true;
            })
        })
        return notificationsToRead
    }

    return (
        <>
            <NotificationBellButton
                unreadNotifications={unreadNotificationsCount}
                lastNotificationIdentifier={notificationData?.lastNotificationId}
                onClick={() => {setShowNotificactionModal(true); 
                                }
                        }
                notificationIconBackGroundColor={notificationIconBackGroundColor}
            />
            <NotificationModal
                showDialog={showNotificactionModal}
                title="Notifications"
                message={message}
                handleClose={() => setShowNotificactionModal(false)}
                handleNavigate={handleGoToNotifications}
                handleMarkAsRead={handleMarkAsRead}
                handleMarkAllAsRead={handleMarkAllAsRead}
                navigateButtonLabel="See more"
                unreadNotifications={unreadNotificationsCount}
                loadingData={loadingData}
                hasError={hasError} 
                notificationData={notificationData?.notifications}
            />
        </>
    )
}

export default NotificationBell